import React, { useState } from "react";
import { TokenMovementResponse, TokenMovementType } from "libs/apiClient";
import { formatDateTime } from "features/discover/functions";
import { Link } from "react-router";
import Modal from "../../../../../assets/js/components/Modal";
import client from "libs/client";
import { useToast } from "ui-kit";
import { _ } from "i18n";

export function TeamTokenMovementsTable({
	teamId,
	tokenMovements,
	tokenBalance,
	tokenEditAccess,
}: {
	teamId: number;
	tokenBalance: number;
	tokenMovements: TokenMovementResponse[];
	tokenEditAccess?: boolean;
}) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
	const [amountInput, setAmountInput] = useState("");
	const [typeInput, setTypeInput] = useState(TokenMovementType.Values.add_purchase);
	const [paymentLinkInput, setPaymentLinkInput] = useState("");
	const [purchasedAtInput, setPurchasedAtInput] = useState("");
	const [noteInput, setNoteInput] = useState("");
	const [removeAmountInput, setRemoveAmountInput] = useState("");
	const [removeNoteInput, setRemoveNoteInput] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const mutation = client.postTokenMovement.useMutation({
		onSuccess: () => {
			addSuccessToast(_(`tokens.modal.message.added`));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverTeamDetail"),
			});
			setIsModalOpen(false);
			resetForm();
			setIsSubmitting(false);
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to add tokens");
			setIsSubmitting(false);
		},
	});

	const removeMutation = client.postTokenMovement.useMutation({
		onSuccess: () => {
			addSuccessToast(_(`tokens.modal.message.removed`));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverTeamDetail"),
			});
			setIsRemoveModalOpen(false);
			resetRemoveForm();
			setIsSubmitting(false);
		},
		onError: (err) => {
			addErrorToast(err.message || "Failed to remove tokens");
			setIsSubmitting(false);
		},
	});

	const resetForm = () => {
		setAmountInput("");
		setTypeInput(TokenMovementType.Values.add_purchase);
		setPaymentLinkInput("");
		setPurchasedAtInput("");
		setNoteInput("");
	};

	const resetRemoveForm = () => {
		setRemoveAmountInput("");
		setRemoveNoteInput("");
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (typeInput === TokenMovementType.Values.add_gift && !noteInput.trim()) {
			addErrorToast("Note is required for gift tokens");
			return;
		}

		setIsSubmitting(true);
		mutation.mutate({
			body: {
				type: typeInput === TokenMovementType.Values.add_purchase ? TokenMovementType.Values.add_purchase : TokenMovementType.Values.add_gift,
				amount: parseInt(amountInput, 10),
				paymentLink: paymentLinkInput || null,
				purchasedAt: purchasedAtInput ? new Date(purchasedAtInput).toISOString() : null,
				note: noteInput || null,
				clubId: null,
				teamId: teamId,
				relatedClubId: null,
				relatedTeamIds: [],
				authorType: "user",
			},
		});
	};

	const handleRemoveSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const amountToRemove = parseInt(removeAmountInput, 10);
		
		if (amountToRemove > tokenBalance) {
			addErrorToast(`Cannot remove ${amountToRemove} tokens. Current balance is only ${tokenBalance} tokens.`);
			return;
		}

		setIsSubmitting(true);
		removeMutation.mutate({
			body: {
				type: "remove_remove",
				amount: amountToRemove,
				paymentLink: null,
				purchasedAt: null,
				note: removeNoteInput || null,
				clubId: null,
				teamId: teamId,
				relatedClubId: null,
				relatedTeamIds: [],
				authorType: "user",
			},
		});
	};

	return (
		<>
			{/* Token Balance and Buttons - Moved above the table */}
			<div
				style={{
					marginBottom: "1rem",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>
					Current token balance: <strong style={{ fontSize: '1.3em', paddingLeft: '1rem' }}>{tokenBalance}</strong>
				</div>
				<div style={{ display: "flex", gap: "0.5rem" }}>
					{tokenEditAccess && (
						<>
							<button onClick={() => setIsModalOpen(true)}>Add Tokens</button>
							<button onClick={() => setIsRemoveModalOpen(true)}>Remove Tokens</button>
						</>
					)}
				</div>
			</div>

			<table className="discover-table">
				<thead>
				<tr>
					<th>Amount</th>
					<th>Type</th>
					<th>Payment Link</th>
					<th>Note</th>
					<th>Purchased</th>
					<th>Created</th>
				</tr>
				</thead>
				<tbody>
				{tokenMovements.map((token: TokenMovementResponse, index) => (
					<tr key={`row-${index}`}>
						<td style={{ color: token.amount > 0 ? 'green' : 'red', fontWeight: 'bold' }}>
							{token.amount > 0 ? `+${token.amount}` : token.amount}
						</td>
						<td>{_(`token.movement.type.${token.movementType}`)}</td>
						<td>{token.paymentLink}</td>
						<td>{token.note}</td>
						<td>{formatDateTime(token.purchasedAt)}</td>
						<td>{formatDateTime(token.createdAt)}</td>
					</tr>
				))}
				</tbody>
			</table>

			<div style={{ marginTop: "1rem", textAlign: "center" }}>
				Showing only 10 last token movements.{" "}
				<Link 
					to={`/discover/token-movements?teamId=${teamId}`}
					target="_blank"
					style={{ textDecoration: "underline" }}
				>
					View all token movements for this team
				</Link>
			</div>

			{isModalOpen && (
				<Modal
					show={isModalOpen}
					title="Add Tokens"
					size="sm"
					onClose={() => setIsModalOpen(false)}
				>
					<form onSubmit={handleSubmit}>
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
							<div style={{ 
								textAlign: "center",
								marginBottom: "1rem"
							}}>
								Current token balance: <strong style={{ fontSize: '1.3em', paddingLeft: '1rem' }}>{tokenBalance}</strong>
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label style={{ width: "120px" }}>
									Type <span style={{ color: 'red' }}>*</span>
								</label>
								<div style={{ display: "flex", gap: "1rem", flex: 1 }}>
									<label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
										<input
											type="radio"
											name="type"
											value={TokenMovementType.Values.add_purchase}
											checked={typeInput === TokenMovementType.Values.add_purchase}
											onChange={(e) => setTypeInput(e.target.value)}
										/>
										Purchase
									</label>
									<label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
										<input
											type="radio"
											name="type"
											value={TokenMovementType.Values.add_gift}
											checked={typeInput === TokenMovementType.Values.add_gift}
											onChange={(e) => setTypeInput(e.target.value)}
										/>
										Gift
									</label>
								</div>
							</div>

							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="amount" style={{ width: "120px" }}>
									Amount <span style={{ color: 'red' }}>*</span>
								</label>
								<input
									id="amount"
									type="number"
									min="1"
									value={amountInput}
									onChange={(e) => setAmountInput(e.target.value)}
									required
									style={{ flex: 1 }}
								/>
							</div>

							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="paymentLink" style={{ width: "120px" }}>
									Payment Link
								</label>
								<input
									id="paymentLink"
									type="text"
									value={paymentLinkInput}
									onChange={(e) => setPaymentLinkInput(e.target.value)}
									style={{ flex: 1 }}
								/>
							</div>

							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="purchasedAt" style={{ width: "120px" }}>
									Purchased At <span style={{ color: 'red' }}>*</span>
								</label>
								<input
									id="purchasedAt"
									type="datetime-local"
									value={purchasedAtInput}
									onChange={(e) => setPurchasedAtInput(e.target.value)}
									required
									style={{ flex: 1 }}
								/>
							</div>

							<div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
								<label
									htmlFor="note"
									style={{ width: "120px", paddingTop: "0.5rem" }}
								>
									Note {typeInput === TokenMovementType.Values.add_gift && <span style={{ color: 'red' }}>*</span>}
								</label>
								<textarea
									id="note"
									rows={3}
									value={noteInput}
									onChange={(e) => setNoteInput(e.target.value)}
									required={typeInput === TokenMovementType.Values.add_gift}
									style={{ flex: 1 }}
								/>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "flex-end",
									gap: "0.5rem",
									marginTop: "1rem",
								}}
							>
								<button type="submit" disabled={isSubmitting}>
									{isSubmitting ? "Submitting..." : "Add tokens"}
								</button>
								<button
									type="button"
									onClick={() => {
										setIsModalOpen(false);
										resetForm();
									}}
								>
									Cancel
								</button>
							</div>
						</div>
					</form>
				</Modal>
			)}

			{isRemoveModalOpen && (
				<Modal
					show={isRemoveModalOpen}
					title="Remove Tokens"
					size="sm"
					onClose={() => setIsRemoveModalOpen(false)}
				>
					<form onSubmit={handleRemoveSubmit}>
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
							<div style={{ 
								textAlign: "center",
								marginBottom: "1rem"
							}}>
								Current token balance: <strong style={{ fontSize: '1.3em', paddingLeft: '1rem' }}>{tokenBalance}</strong>
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
								<label htmlFor="removeAmount" style={{ width: "120px" }}>
									Amount <span style={{ color: 'red' }}>*</span>
								</label>
								<input
									id="removeAmount"
									type="number"
									min="1"
									max={tokenBalance}
									value={removeAmountInput}
									onChange={(e) => setRemoveAmountInput(e.target.value)}
									required
									style={{ flex: 1 }}
								/>
							</div>

							<div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
								<label
									htmlFor="removeNote"
									style={{ width: "120px", paddingTop: "0.5rem" }}
								>
									Note <span style={{ color: 'red' }}>*</span>
								</label>
								<textarea
									id="removeNote"
									rows={3}
									value={removeNoteInput}
									onChange={(e) => setRemoveNoteInput(e.target.value)}
									required
									style={{ flex: 1 }}
								/>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "flex-end",
									gap: "0.5rem",
									marginTop: "1rem",
								}}
							>
								<button type="submit" disabled={isSubmitting}>
									{isSubmitting ? "Submitting..." : "Remove tokens"}
								</button>
								<button
									type="button"
									onClick={() => {
										setIsRemoveModalOpen(false);
										resetRemoveForm();
									}}
								>
									Cancel
								</button>
							</div>
						</div>
					</form>
				</Modal>
			)}
		</>
	);
}
